<template>
  <div :class="['header-container', { 'header-displaced': !['Login', 'TokenExpired', 'NotFound'].includes($route.name) }]">
    <!-- Left column: logo + divider + title (1/3 width) -->
    <div class="header-left" ref="headerLeft">
      <div class="logo-container">
        <img :src="logo" alt="Logo" class="logo" @error="handleLogoError" />
      </div>
      <div class="header-title">
        <div class="title-inline">
          <h1 v-fit-text class="brand-title">{{ $t('gestion_tickets') }}</h1>
        </div>
      </div>
    </div>

    <!-- Right column: resto de elementos (2/3 width) -->
    <div class="header-right">
      <div class="icon-row-horizontal" v-if="!['TokenExpired', 'NotFound'].includes($route.name)">
        <!-- Ordered group: Inicio, Gestion usuarios, Mantenimiento tablas, Ayuda, Cambio idioma, Mi cuenta -->
        <!-- Mostrar 'Inicio' también cuando estamos en Manual para permitir salir -->
        <button v-if="(userRole === 1 && $route.name !== 'Welcome') || $route.name === 'Manual'" class="icon-btn elongated primary-action" type="button" @click="goToWelcome" :title="$t('inicio')" :aria-label="$t('inicio')" :data-tooltip="$t('inicio')">
          <span class="btn-label">{{ $t('inicio') }}</span>
          <span class="material-icons" aria-hidden="true">home</span>
        </button>
        <!-- botones administrativos: versión escritorio (dentro de esta fila) -->
        <div class="desktop-admin" v-if="userRole === 1">
          <router-link :to="{ name: 'UserManagement' }" class="icon-btn manage-users elongated primary-action" v-if="$route.name !== 'UserManagement'" @click.stop :title="$t('gestion_usuarios')" :aria-label="$t('gestion_usuarios')" :data-tooltip="$t('gestion_usuarios')">
            <span class="btn-label">{{ $t('gestion_usuarios') }}</span>
            <span class="material-icons" aria-hidden="true">manage_accounts</span>
          </router-link>

          <button class="icon-btn table-maintenance elongated primary-action" v-if="$route.name !== 'TableManagement'" @click.stop="goToTableMaintenance" type="button" :title="$t('mantenimiento_tablas')" :aria-label="$t('mantenimiento_tablas')" :data-tooltip="$t('mantenimiento_tablas')">
            <span class="btn-label">{{ $t('mantenimiento_tablas') }}</span>
            <span class="material-icons" aria-hidden="true">settings</span>
          </button>
        </div>

  <router-link v-if="userStore.token" to="/manual" class="manual-icon" :title="$t('manual.titulo')">
          <button class="icon-btn elongated primary-action" type="button" :aria-label="$t('manual.titulo')" :data-tooltip="$t('ayuda')">
            <span class="btn-label">{{ $t('ayuda') }}</span>
            <svg class="help-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 19.43 19.44" fill="currentColor" aria-hidden="true">
              <g>
                <path d="M9.72,1.37h0c2.58,0,5.05,1.23,6.62,3.27,1.57,2.05,2.11,4.75,1.44,7.24-.67,2.49-2.48,4.57-4.86,5.56-1.02.43-2.1.64-3.21.64-1.45,0-2.87-.38-4.13-1.09-.21-.13-.46-.2-.71-.2-.17,0-.34.03-.51.1l-2.07.83.46-2.74c.05-.32,0-.65-.18-.93-1.02-1.7-1.41-3.71-1.09-5.66.32-1.95,1.32-3.74,2.83-5.02,1.51-1.28,3.43-1.99,5.41-1.99M9.72,0c-2.31,0-4.54.82-6.3,2.31C1.67,3.81.5,5.88.13,8.16c-.37,2.28.08,4.61,1.27,6.59l-.64,3.82c-.05.27.06.54.27.71.14.11.31.16.48.16.09,0,.18-.02.27-.05l3.09-1.24h0c1.49.86,3.16,1.29,4.84,1.29,1.27,0,2.53-.25,3.73-.74,2.79-1.16,4.88-3.55,5.66-6.46.78-2.92.16-6.03-1.68-8.42C15.58,1.41,12.74,0,9.72,0h0Z"/>
                <path d="M9.72,14.95c-.3,0-.57-.18-.69-.46-.12-.28-.05-.6.16-.81.21-.21.54-.28.81-.16s.46.39.46.69c0,.2-.08.39-.22.53-.14.14-.33.22-.53.22h0Z"/>
                <path d="M11.37,10.62c-.46.46-.9.9-.9,1.34,0,.41-.33.75-.75.75s-.75-.33-.75-.75c.1-.95.58-1.82,1.34-2.4.46-.46.9-.9.9-1.34,0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5,1.5c0,.41-.33.75-.75.75s-.75-.33-.75-.75c0-1.07.57-2.06,1.5-2.59.92-.53,2.07-.53,2.99,0,.93.53,1.5,1.52,1.5,2.59-.1.95-.58,1.82-1.34,2.4h0Z"/>
              </g>
            </svg>
          </button>
        </router-link>

        <div class="language-selector-inline" style="margin-left:8px;" ref="langSelectorContainer">
          <div class="custom-lang-select" @click.stop="toggleLangDropdown" role="button" aria-haspopup="listbox" :aria-expanded="langDropdownOpen">
            <span class="lang-label">{{ getLangLabel(selectedLocale) }}</span>
            <span class="arrow" :class="{open: langDropdownOpen}">▼</span>
          </div>

          <!-- Teleportar el dropdown al body para evitar stacking-contexts y overlays del WelcomeView -->
          <teleport to="body">
            <ul v-if="langDropdownOpen" class="lang-dropdown lang-dropdown-teleported" :style="langDropdownTeleportStyle" role="listbox">
              <li v-for="lang in languages" :key="lang.value" @click.stop.prevent="selectLang(lang.value)">
                <span>{{ lang.label }}</span>
              </li>
            </ul>
          </teleport>
        </div>

  <div v-if="$route.name !== 'Login'" class="user-account-wrapper" ref="userAccountWrapper">
          <button
            ref="userAccountBtn"
            class="icon-btn user-account"
            @click.stop="onUserBtnClick($event)"
            type="button"
            :title="$t('mi_cuenta')"
            :aria-label="$t('mi_cuenta')"
            :aria-hidden="$route.name === 'Login'"
            :disabled="$route.name === 'Login'"
            :tabindex="$route.name === 'Login' ? -1 : 0"
            aria-haspopup="true"
            :aria-expanded="userMenuOpen"
            :data-tooltip="$t('mi_cuenta')"
          >
            <span class="btn-label" style="display:none">{{ $t('mi_cuenta') }}</span>
            <img :src="userAccountIcon" alt="User account" class="user-account-icon" />
          </button>
          <div class="user-menu" v-if="userMenuOpen" role="menu" @click.stop>
            <button ref="firstMenuItem" class="user-menu-item" type="button" role="menuitem" tabindex="0" @click="openChangePasswordModal">{{ $t('cambiar_contrasena') }}</button>
            <button class="user-menu-item logout" type="button" role="menuitem" tabindex="0" @click="handleLogout">
              <span>{{ $t('cerrar_sesion') }}</span>
              <!-- Inline SVG para que herede currentColor (color corporativo) -->
              <svg class="logout-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18.43 15.35" fill="currentColor" aria-hidden="true" focusable="false">
                <g>
                  <path d="M7.87,15.35H2.21c-1.22,0-2.21-.99-2.21-2.2V2.2C0,.99.99,0,2.21,0h5.67c1.22,0,2.21.99,2.21,2.2v2.4c0,.41-.34.75-.75.75s-.75-.34-.75-.75v-2.4c0-.39-.32-.7-.71-.7H2.21c-.39,0-.71.32-.71.7v10.94c0,.39.32.7.71.7h5.67c.39,0,.71-.32.71-.7v-2.4c0-.41.34-.75.75-.75s.75.34.75.75v2.4c0,1.22-.99,2.2-2.21,2.2Z"/>
                  <path d="M17.68,8.42H5.04c-.41,0-.75-.34-.75-.75s.34-.75.75-.75h10.83l-2.83-2.83c-.29-.29-.29-.77,0-1.06s.77-.29,1.06,0l4.11,4.11c.21.21.28.54.16.82-.12.28-.39.46-.69.46Z"/>
                  <path d="M13.57,12.54c-.19,0-.38-.07-.53-.22-.29-.29-.29-.77,0-1.06l2.83-2.83H5.04c-.41,0-.75-.34-.75-.75s.34-.75.75-.75h12.64c.3,0,.58.18.69.46.12.28.05.6-.16.82l-4.11,4.11c-.15.15-.34.22-.53.22Z"/>
                </g>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Tercera fila (móvil): botones administrativos reubicados aquí -->
    <div class="admin-row" v-if="userRole === 1 && !['TokenExpired', 'NotFound'].includes($route.name)">
      <router-link :to="{ name: 'UserManagement' }" class="icon-btn manage-users elongated primary-action" v-if="$route.name !== 'UserManagement'" @click.stop :title="$t('gestion_usuarios')" :aria-label="$t('gestion_usuarios')" :data-tooltip="$t('gestion_usuarios')">
        <span class="btn-label">{{ $t('gestion_usuarios') }}</span>
        <span class="material-icons" aria-hidden="true">manage_accounts</span>
      </router-link>

  <button class="icon-btn table-maintenance elongated primary-action" v-if="$route.name !== 'TableManagement'" @click.stop="goToTableMaintenance" type="button" :title="$t('mantenimiento_tablas')" :aria-label="$t('mantenimiento_tablas')" :data-tooltip="$t('mantenimiento_tablas')">
        <span class="btn-label">{{ $t('mantenimiento_tablas') }}</span>
        <span class="material-icons" aria-hidden="true">settings</span>
      </button>
    </div>
  </div>

  <UserLogModal
    :visible="showLogsModal"
    :logs="logs"
    :title="$t('logs_todos_usuarios')"
    @close="showLogsModal = false"
  />

  <!-- Modal para cambiar la contraseña de un usuario -->
  <ChangePasswordModalHeader
    :visible="showChangePasswordModal"
    :user-name="selectedUserName"
    :user-id="selectedUserId"
    @close="closeChangePasswordModal"
    @password-changed="handlePasswordChanged"
  />
</template>

<script>
import logo from '../assets/logo.svg';
import userAccountIcon from '../assets/usuariocuenta.svg';
import logoutIcon from '../assets/cerrarsesion.svg';
import api from '@/api.js';
import UserLogModal from '@/views/UserLogModal.vue';
import ChangePasswordModalHeader from '@/components/changePasswordModalHeader.vue';
import { useUserStore } from '@/stores/user';
import Swal from 'sweetalert2';

export default {
  components: {
    UserLogModal,
    ChangePasswordModalHeader,
  },
  directives: {
    fitText: {
      mounted(el) {
        el.style.whiteSpace = 'nowrap';
        el.style.overflow = 'hidden';
        el.style.textOverflow = 'clip';
        el.style.display = 'block';

        const computeAdjust = () => {
          const computed = window.getComputedStyle(el);
          const maxFont = parseFloat(computed.fontSize) || 20;
          let fontSize = maxFont;
          el.style.fontSize = fontSize + 'px';
          while (el.scrollWidth > el.clientWidth && fontSize > 10) {
            fontSize -= 1;
            el.style.fontSize = fontSize + 'px';
          }
          while (el.scrollWidth <= el.clientWidth && fontSize < maxFont) {
            fontSize += 1;
            el.style.fontSize = fontSize + 'px';
            if (el.scrollWidth > el.clientWidth) {
              fontSize -= 1;
              el.style.fontSize = fontSize + 'px';
              break;
            }
          }
        };

        el.__fitText = { adjust: computeAdjust };
        if ('ResizeObserver' in window) {
          el.__fitText.ro = new ResizeObserver(() => computeAdjust());
          el.__fitText.ro.observe(el);
        }
        window.addEventListener('resize', computeAdjust);
        computeAdjust();
        setTimeout(computeAdjust, 100);
      },
      updated(el) {
        el.__fitText?.adjust?.();
      },
      unmounted(el) {
        if (el.__fitText?.ro) {
          try { el.__fitText.ro.disconnect(); } catch (e) {}
        }
        window.removeEventListener('resize', el.__fitText?.adjust);
        delete el.__fitText;
      }
    }
  },
  setup() {
    const userStore = useUserStore();
    return { userStore };
  },
  data() {
    return {
      logo,
      userAccountIcon,
      logoutIcon,
      logs: [],
      userMenuOpen: false,
      showLogsModal: false,
      // normalize selectedLocale: prefer stored value, otherwise read current i18n locale (handle ref or string)
      selectedLocale: (function() {
        try {
          const stored = localStorage.getItem('selectedLocale');
          if (stored) return stored;
          if (typeof window === 'undefined') return 'es';
          // several shapes: this.$i18n.global.locale.value OR this.$i18n.locale (string or ref)
          const i18n = (typeof window !== 'undefined') ? (typeof __VUE_DEVTOOLS_GLOBAL_HOOK__ !== 'undefined' ? null : null) : null; // placeholder to avoid lint noise
          // use this.$i18n at runtime in created/mounted; fallback to '' if not available at module load
          try {
            // 'this' isn't available here at data() evaluation in some cases, so we'll lazily resolve in mounted if needed
          } catch (e) {}
          return localStorage.getItem('selectedLocale') || (typeof (window && window.__INITIAL_LOCALE__) !== 'undefined' ? window.__INITIAL_LOCALE__ : 'es');
        } catch (e) { return 'es'; }
      })(),
      languages: [
        { value: 'es', label: 'Español', flag: '/img/flags/es.webp' },
        { value: 'en', label: 'English', flag: '/img/flags/gb.webp' },
        { value: 'fr', label: 'Français', flag: '/img/flags/fr.webp' },
        { value: 'it', label: 'Italiano', flag: '/img/flags/it.webp' },
        { value: 'pt', label: 'Português', flag: '/img/flags/pt.webp' },
        { value: 'ro', label: 'Română', flag: '/img/flags/ro.webp' },
      ],
      langDropdownOpen: false,
  // Inline style applied to teleported dropdown (top/left/width/zIndex)
  langDropdownTeleportStyle: {},
      isMobile: window.innerWidth <= 600,
      showChangePasswordModal: false,
      showNewPassword: false,
      showConfirmPassword: false,
      showCurrentPassword: false,
      selectedUserName: '',
      selectedUserId: null,
      passwordForm: {
        old_password: '',
        new_password: '',
        new_password_confirmation: '',
        forzar_cambio_contrasena: true,
      },
      isSaving: false,
    };
  },
  computed: {
    userName() {
      const name = this.userStore.name || '';
      if (name.length > 30) {
        return name.slice(0, 27) + '...';
      }
      return name;
    },
    userRole() {
      return this.userStore.role;
    },
  },
  watch: {
    showChangePasswordModal(val) {
      document.body.style.overflow = val ? 'hidden' : '';
      document.body.style.position = val ? 'relative' : '';
      if (val) {
        this.$nextTick(() => { this.$forceUpdate(); });
        window.addEventListener('resize', this._updateModalOverlayHeight);
        window.addEventListener('scroll', this._updateModalOverlayHeight);
      } else {
        window.removeEventListener('resize', this._updateModalOverlayHeight);
        window.removeEventListener('scroll', this._updateModalOverlayHeight);
      }
    }
  },
  methods: {
    _updateModalOverlayHeight() {
      this.$forceUpdate();
    },
    openChangePasswordModal() {
      // Prepare selected user data (header always uses current user)
      this.selectedUserId = this.userStore.id;
      this.selectedUserName = this.userStore.name || '';
      this.passwordForm = {
        old_password: '',
        new_password: '',
        new_password_confirmation: ''
      };

  // Always open the header modal (the header will be the source of truth).
  // Also include a marker in the route query/event so that UserManagementView
  // can detect the request came from the header and avoid opening its own modal.
  this.showChangePasswordModal = true;
      // If we're on the UserManagement route, update its query so it can react and open its modal.
      try {
        const targetRouteName = 'UserManagement';
        const timestamp = Date.now();
        if (this.$route && this.$route.name === targetRouteName) {
          const newQuery = Object.assign({}, this.$route.query || {});
          // use a unique value so route watcher triggers even if param existed
          newQuery.openChangePassword = String(timestamp);
          newQuery.openChangePasswordSource = 'header';
          this.$router.replace({ name: targetRouteName, query: newQuery }).catch(() => {});
        } else {
          this.$router.push({ name: targetRouteName, query: { openChangePassword: String(timestamp), openChangePasswordSource: 'header' } }).catch(() => {});
        }
      } catch (e) {
        // fallback: try dispatching an event
  try { window.dispatchEvent(new CustomEvent('open-change-password', { detail: { userId: this.userStore.id, source: 'header' } })); } catch (err) {}
      }
      this.closeUserMenu();
    },
    closeChangePasswordModal() {
      this.showChangePasswordModal = false;
    },
    toggleShowNewPassword() {
      this.showNewPassword = !this.showNewPassword;
    },
    toggleShowConfirmPassword() {
      this.showConfirmPassword = !this.showConfirmPassword;
    },
    toggleShowCurrentPassword() {
      this.showCurrentPassword = !this.showCurrentPassword;
    },
    async handleChangePassword() {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        // Client-side validation before sending to server
        const current = String(this.passwordForm.old_password || '');
        const pwd = String(this.passwordForm.new_password || '');
        const confirm = String(this.passwordForm.new_password_confirmation || '');

        if (pwd.length < 12) {
          await Swal.fire({ icon: 'error', title: this.$t('error'), text: this.$t('new_password_min_length') || 'La nueva contraseña debe tener al menos 12 caracteres.', confirmButtonColor: '#99172A' });
          this.isSaving = false;
          return;
        }

        // Regex must match: at least one lowercase, one uppercase, one digit and one special char from wide set
        const pwdRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#\$%\^&\*()_+\-=[\]{}|;:,.<>?]).{12,}$/;
        if (!pwdRegex.test(pwd)) {
          await Swal.fire({ icon: 'error', title: this.$t('error'), text: this.$t('contrasenanovalida') || 'La nueva contraseña no cumple los requisitos de complejidad.', confirmButtonColor: '#99172A' });
          this.isSaving = false;
          return;
        }

        if (pwd !== confirm) {
          await Swal.fire({ icon: 'error', title: this.$t('error'), text: this.$t('contrasenasnocoinciden') || 'Las contraseñas no coinciden.', confirmButtonColor: '#99172A' });
          this.isSaving = false;
          return;
        }

        if (current && current === pwd) {
          await Swal.fire({ icon: 'error', title: this.$t('error'), text: this.$t('new_password_different') || 'La nueva contraseña debe ser diferente a la actual.', confirmButtonColor: '#99172A' });
          this.isSaving = false;
          return;
        }

        // All client-side checks passed — send to server
        await api.post('/users/change-password', {
          current_password: current,
          new_password: pwd,
          new_password_confirmation: confirm
        });

        try {
          await api.post('/logs', {
            accion: 'cambio_contrasena',
            usuario_id: this.userStore.id,
            descripcion: 'Usuario cambió su contraseña',
            fecha: new Date().toISOString()
          });
        } catch (logErr) {
          console.warn('No se pudo registrar el log de cambio de contraseña:', logErr);
        }

        await Swal.fire({ icon: 'success', title: this.$t('contraseña_actualizada'), confirmButtonColor: '#99172A' });
        this.closeChangePasswordModal();
      } catch (err) {
        const errorMessage = err.response?.data?.message || this.$t('error_cambiar_contrasena');
        await Swal.fire({ icon: 'error', title: 'Error', text: errorMessage, confirmButtonColor: '#99172A' });
      } finally {
        this.isSaving = false;
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 600;
      this.$forceUpdate();
    },
    changeLocale() {
      this.$i18n.locale = this.selectedLocale;
      localStorage.setItem('selectedLocale', this.selectedLocale);
    },
    getFlag(locale) {
      const lang = this.languages.find(l => l.value === locale);
      return lang ? lang.flag : '';
    },
    getLangLabel(locale) {
      const lang = this.languages.find(l => l.value === locale);
      return lang ? lang.label : locale;
    },
    toggleLangDropdown() {
      if (this.userMenuOpen) {
        this.closeUserMenu();
      }
      
      this.langDropdownOpen = !this.langDropdownOpen;
      
      if (this.langDropdownOpen) {
        this.$nextTick(() => {
          try { document.addEventListener('pointerdown', this.handleLangOutsideClick, true); } catch (e) {}
          try { window.addEventListener('keydown', this._onLangKeydown); } catch (e) {}
          // compute teleported dropdown position so it sits below the button and on top of other contexts
          this.$nextTick(() => this._computeLangTeleportPosition());
        });
      } else {
        try { document.removeEventListener('pointerdown', this.handleLangOutsideClick, true); } catch (e) {}
        try { window.removeEventListener('keydown', this._onLangKeydown); } catch (e) {}
        this.langDropdownTeleportStyle = {};
      }
    },

    _computeLangTeleportPosition() {
      try {
        const container = this.$refs.langSelectorContainer;
        if (!container) return;
        const btn = container.querySelector('.custom-lang-select');
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const viewportW = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
        const dropdownMinWidth = 140;
        const left = Math.max(8, Math.round(rect.left));
        // prefer aligning dropdown to the left edge of button but ensure it doesn't overflow viewport
        const width = Math.max(dropdownMinWidth, Math.round(rect.width));
        let computedLeft = left;
        if (computedLeft + width + 12 > viewportW) {
          computedLeft = Math.max(8, viewportW - width - 12);
        }
        const top = Math.round(rect.bottom + 6 + (window.scrollY || window.pageYOffset));
        // set a very high z-index to outrank WelcomeView elements that use huge z-index values
        const z = 99999999;
        // match dropdown width to the button width for visual alignment, but never below dropdownMinWidth
        const finalWidth = Math.max(dropdownMinWidth, width);
        this.langDropdownTeleportStyle = {
          position: 'absolute',
          top: top + 'px',
          left: computedLeft + 'px',
          width: finalWidth + 'px',
          zIndex: z,
          boxSizing: 'border-box'
        };
      } catch (e) {
        // ignore
      }
    },
    handleLangOutsideClick(e) {
      try {
        const container = this.$refs.langSelectorContainer;
        // teleported dropdown element (appended to body)
        const tele = document.querySelector('.lang-dropdown-teleported');

        // Prefer composedPath when available (handles Shadow DOM). Fallback to path or built-in contains.
        const path = (typeof e.composedPath === 'function') ? e.composedPath() : (e.path || null);

        const clickedInsideContainer = container && (container.contains(e.target) || (Array.isArray(path) && path.indexOf(container) !== -1));
        const clickedInsideTele = tele && (tele.contains(e.target) || (Array.isArray(path) && path.indexOf(tele) !== -1));

        // If click was neither inside the selector container nor inside the teleported dropdown, close it
        if (!clickedInsideContainer && !clickedInsideTele) {
          this.langDropdownOpen = false;
          try { document.removeEventListener('pointerdown', this.handleLangOutsideClick, true); } catch (err) {}
          try { window.removeEventListener('keydown', this._onLangKeydown); } catch (err) {}
        }
      } catch (err) {
        // swallow
        try { document.removeEventListener('pointerdown', this.handleLangOutsideClick, true); } catch (e) {}
        try { window.removeEventListener('keydown', this._onLangKeydown); } catch (e) {}
        this.langDropdownOpen = false;
      }
    },
    _onLangKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        this.langDropdownOpen = false;
        try { document.removeEventListener('pointerdown', this.handleLangOutsideClick, true); } catch (err) {}
        try { window.removeEventListener('keydown', this._onLangKeydown); } catch (err) {}
      }
    },
    toggleUserMenu() {
      if (this.langDropdownOpen) {
        this.langDropdownOpen = false;
        try { window.removeEventListener('click', this.handleLangOutsideClick); } catch (e) {}
        try { window.removeEventListener('keydown', this._onLangKeydown); } catch (e) {}
      }
      
      this.userMenuOpen = !this.userMenuOpen;
      
      if (this.userMenuOpen) {
        if (import.meta.env && import.meta.env.DEV) console.debug('[Header] opening user menu');
        this.$nextTick(() => {
          // Usar pointerdown en capture sobre document para detectar clicks fuera incluso cuando otros handlers detienen la propagación
          try { document.addEventListener('pointerdown', this.handleOutsideClick, true); } catch (e) {}
          try { window.addEventListener('keydown', this._onGlobalKeydown); } catch (e) {}

          // Enfocar el primer elemento si existe
          try {
            const first = this.$refs.firstMenuItem;
            if (first) first.focus();
            if (import.meta.env && import.meta.env.DEV) console.debug('[Header] focused first menu item:', !!first);
          } catch (e) {}
        });
      } else {
        if (import.meta.env && import.meta.env.DEV) console.debug('[Header] closing user menu (toggle)');
        try { document.removeEventListener('pointerdown', this.handleOutsideClick, true); } catch (e) {}
        try { window.removeEventListener('keydown', this._onGlobalKeydown); } catch (e) {}
        this.$nextTick(() => { 
          try { 
            const btn = this.$refs.userAccountBtn;
            if (btn) btn.focus(); 
          } catch (e) {} 
        });
      }
    },
    closeUserMenu() {
      if (import.meta.env && import.meta.env.DEV) console.debug('[Header] closeUserMenu called');
      this.userMenuOpen = false;
      try { document.removeEventListener('pointerdown', this.handleOutsideClick, true); } catch (e) {}
      try { window.removeEventListener('keydown', this._onGlobalKeydown); } catch (e) {}
    },
    handleOutsideClick(e) {
      if (import.meta.env && import.meta.env.DEV) console.debug('[Header] handleOutsideClick event target=', e.target);
      try {
        const wrapper = this.$refs.userAccountWrapper;
        if (!wrapper) return;

        // Prefer composedPath (handles Shadow DOM & composed events). Fallback a path o contains
        const path = (typeof e.composedPath === 'function') ? e.composedPath() : (e.path || null);
        let clickedInside = false;

        if (path && Array.isArray(path)) {
          for (let i = 0; i < path.length; i++) {
            const node = path[i];
            if (node === wrapper) { clickedInside = true; break; }
          }
        } else {
          clickedInside = wrapper.contains(e.target);
        }

        if (!clickedInside) {
          if (import.meta.env && import.meta.env.DEV) console.debug('[Header] outside pointer detected — closing menu');
          this.closeUserMenu();
        }
      } catch (err) {
        if (import.meta.env && import.meta.env.DEV) console.warn('[Header] handleOutsideClick error', err);
      }
    },
    onUserBtnClick(ev) {
      // No hacer nada cuando estamos en la pantalla de login
      if (this.$route && this.$route.name === 'Login') {
        if (import.meta.env && import.meta.env.DEV) console.debug('[Header] user button click ignored on Login screen');
        return;
      }
      // delegar al toggle existente
      try {
        if (import.meta.env && import.meta.env.DEV) console.debug('[Header] onUserBtnClick called, route=', this.$route && this.$route.name ? this.$route.name : this.$route);
        this.toggleUserMenu(ev);
      } catch (e) { if (import.meta.env && import.meta.env.DEV) console.error(e); }
    },
    _onGlobalKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        this.closeUserMenu();
      }
    },
    selectLang(locale) {
      try {
        this.selectedLocale = String(locale || '').trim();
        this.changeLocale();
      } catch (e) {}
      this.langDropdownOpen = false;
      try { document.removeEventListener('pointerdown', this.handleLangOutsideClick, true); } catch (e) {}
      try { window.removeEventListener('keydown', this._onLangKeydown); } catch (e) {}
    },
    async openLogsModal() {
      try {
        const response = await api.get('/logs');
        this.logs = response.data.logs || [];
        this.showLogsModal = true;
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: this.$t('error_cargar_logs'), timer: 2500, showConfirmButton: false });
      }
    },
    goToWelcome() {
      if (import.meta.env && import.meta.env.DEV) console.debug('[Header] goToWelcome called, current route=', this.$route && this.$route.name ? this.$route.name : this.$route);
      this.$router.push({ name: 'Welcome' })
        .then(() => { if (import.meta.env && import.meta.env.DEV) console.debug('[Header] navigated to Welcome'); })
        .catch(err => { if (import.meta.env && import.meta.env.DEV) console.error('[Header] navigation to Welcome failed', err); });
    },
    goToUserManagement() {
      if (import.meta.env && import.meta.env.DEV) console.debug('[Header] goToUserManagement called, current route=', this.$route && this.$route.name ? this.$route.name : this.$route);
      this.$router.push({ name: 'UserManagement' })
        .then(() => { if (import.meta.env && import.meta.env.DEV) console.debug('[Header] navigated to UserManagement'); })
        .catch(err => { if (import.meta.env && import.meta.env.DEV) console.error('[Header] navigation to UserManagement failed', err); });
    },
    goToTableMaintenance() {
      if (import.meta.env && import.meta.env.DEV) console.debug('[Header] goToTableMaintenance called, current route=', this.$route && this.$route.name ? this.$route.name : this.$route);
      this.$router.push({ name: 'TableManagement' })
        .then(() => { if (import.meta.env && import.meta.env.DEV) console.debug('[Header] navigated to TableManagement'); })
        .catch(err => { if (import.meta.env && import.meta.env.DEV) console.error('[Header] navigation to TableMaintenance failed', err); });
    },
    goBack() {
      this.$router.go(-1);
    },
    async handleLogout() {
      try {
        await api.post('/logout');
      } catch (error) {
        if (import.meta.env.DEV) console.error('Logout error:', error);
      } finally {
        // cerrar el menú si está abierto
        try { this.closeUserMenu(); } catch (e) {}
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('user_name');
        sessionStorage.removeItem('user_role');
        this.userStore.clearUser();
        this.$router.push({ name: 'Login' });
      }
    },
    showMobileTooltip(el) {
      this.clearMobileTooltip();
      const txt = el.getAttribute('data-tooltip');
      if (!txt) return;
      const tip = document.createElement('div');
      tip.className = 'mobile-tooltip';
      tip.textContent = txt;
      document.body.appendChild(tip);
      const rect = el.getBoundingClientRect();
      tip.style.left = Math.min(window.innerWidth - tip.offsetWidth - 8, Math.max(8, rect.left + rect.width/2 - tip.offsetWidth/2)) + 'px';
      tip.style.top = Math.max(8, rect.top - tip.offsetHeight - 8) + 'px';
      this.__currentTooltip = tip;
      this.__tooltipTimeout = setTimeout(() => this.clearMobileTooltip(), 2000);
    },
    clearMobileTooltip() {
      if (this.__tooltipTimeout) { clearTimeout(this.__tooltipTimeout); this.__tooltipTimeout = null; }
      if (this.__currentTooltip) { try { this.__currentTooltip.remove(); } catch (e) {} this.__currentTooltip = null; }
    }
    ,
    updateHeaderHeight() {
      try {
        const el = (this.$el && this.$el.querySelector) ? this.$el : document.querySelector('body');
        const header = el.querySelector('.header-container') || document.querySelector('.header-container');
        const h = header ? Math.ceil(header.getBoundingClientRect().height) : 64;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
      } catch (e) {
        // ignore
      }
    },
    ensureLogoVisible() {
      try {
        const root = (this.$el && this.$el.querySelector) ? this.$el : document;
        const img = root.querySelector('.logo');
        if (!img) return;
        // force visible at runtime (inline styles override most CSS rules)
        img.style.display = 'block';
        img.style.visibility = 'visible';
        img.style.opacity = '1';
        img.style.position = 'relative';
        img.style.zIndex = '210';
        img.style.margin = '0';
        // adjust max-height based on viewport
        if (window.innerWidth <= 600) {
          img.style.maxHeight = '48px';
          img.style.width = '96px';
        } else {
          img.style.maxHeight = '75px';
          img.style.width = 'auto';
        }
      } catch (e) {
        // ignore
      }
    },
    alignLogoToTable() {
      try {
        const headerLeft = this.$refs.headerLeft;
        if (!headerLeft) return;
        const logoContainer = headerLeft.querySelector('.logo-container');
        if (!logoContainer) return;

        const table = document.querySelector('.table-container');
        // reset any previous adjustments when table is absent
        if (!table) {
          logoContainer.style.marginLeft = '';
          return;
        }

        const tableRect = table.getBoundingClientRect();
        const bodyRect = document.documentElement.getBoundingClientRect();
        // desired left offset so logo left aligns with table left (relative to document)
        const desiredLeft = Math.max(8, Math.round(tableRect.left - bodyRect.left));

        // current logo left (relative to document)
        const logoRect = logoContainer.getBoundingClientRect();
        const logoLeft = Math.round(logoRect.left - bodyRect.left);

        const delta = desiredLeft - logoLeft;
        if (delta > 0) {
          // move only the logo container to the right by delta pixels
          logoContainer.style.marginLeft = delta + 'px';
        } else {
          // no adjustment needed
          logoContainer.style.marginLeft = '';
        }
      } catch (err) {
        // swallow
      }
    },
    handleLogoError(e) {
      // If the logo fails to load, replace it with a small inline SVG placeholder once.
      try {
        const img = e && e.target ? e.target : null;
        if (!img) return;
        // avoid infinite loop: if we already set fallback, hide
        if (img.dataset && img.dataset.fallbackSet) {
          img.style.display = 'none';
          return;
        }
        // simple inline SVG placeholder (Agrovin initials) as data URI
        const svg = encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="160" height="40"><rect width="100%" height="100%" fill="#ffffff"/><text x="8" y="28" font-family="sans-serif" font-size="20" fill="#99172A">Agrovin</text></svg>');
        img.src = 'data:image/svg+xml;utf8,' + svg;
        if (img.dataset) img.dataset.fallbackSet = '1';
        img.style.display = 'block';
        img.style.opacity = '1';
        img.style.visibility = 'visible';
      } catch (err) {
        try { const img = e && e.target ? e.target : null; if (img) img.style.display = 'none'; } catch (e2) {}
      }
    },
  },
  mounted() {
    console.log('[Header] mounted, route=', this.$route && this.$route.name ? this.$route.name : this.$route);
    this.__tooltipHandlers = [];
    // ensure root for querySelectorAll — fallback to document when $el isn't a DOM node
    const rootForQuery = (this.$el && typeof this.$el.querySelectorAll === 'function') ? this.$el : document;
    const buttons = rootForQuery.querySelectorAll('.icon-btn[data-tooltip]');
    buttons.forEach(btn => {
      let pressTimer = null;
      const start = (e) => {
        if (e.type === 'click' && e.button !== 0) return;
        pressTimer = setTimeout(() => {
          this.showMobileTooltip(btn);
        }, 500);
      };
      const cancel = () => { if (pressTimer) { clearTimeout(pressTimer); pressTimer = null; } };
      btn.addEventListener('touchstart', start, { passive: true });
      btn.addEventListener('mousedown', start);
      btn.addEventListener('touchend', cancel);
      btn.addEventListener('mouseleave', cancel);
      btn.addEventListener('mouseup', cancel);
      this.__tooltipHandlers.push({ btn, start, cancel });
    });
    // expose header height as CSS variable so other views (manual aside) can align
    try {
      this.updateHeaderHeight();
      this.alignLogoToTable();
      this.ensureLogoVisible();
      window.addEventListener('resize', this.updateHeaderHeight);
      window.addEventListener('resize', this.alignLogoToTable);
      window.addEventListener('resize', this.ensureLogoVisible);
    } catch (e) {}
  },
  beforeUnmount() {
    try { document.removeEventListener('pointerdown', this.handleOutsideClick, true); } catch (e) {}
    try { window.removeEventListener('keydown', this._onGlobalKeydown); } catch (e) {}
    try { document.removeEventListener('pointerdown', this.handleLangOutsideClick, true); } catch (e) {}
    try { window.removeEventListener('keydown', this._onLangKeydown); } catch (e) {}
  try { window.removeEventListener('resize', this.updateHeaderHeight); } catch (e) {}
  try { window.removeEventListener('resize', this.alignLogoToTable); } catch (e) {}
  try { window.removeEventListener('resize', this.ensureLogoVisible); } catch (e) {}
    if (this.__tooltipHandlers) {
      this.__tooltipHandlers.forEach(h => {
        try { h.btn.removeEventListener('touchstart', h.start); } catch (e) {}
        try { h.btn.removeEventListener('mousedown', h.start); } catch (e) {}
        try { h.btn.removeEventListener('touchend', h.cancel); } catch (e) {}
        try { h.btn.removeEventListener('mouseleave', h.cancel); } catch (e) {}
        try { h.btn.removeEventListener('mouseup', h.cancel); } catch (e) {}
      });
    }
  }
};

</script>

<style scoped>
.btn-close {
  filter: invert(1);
  opacity: 0.8;
  color: white;
}

.btn-close:hover {
  opacity: 1;
}

.header-title h1 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
  margin: 0;
  line-height: 1;
  font-size: 16px; /* aumentado ~33% */
}

.custom-lang-select {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: transparent;
  color: #000000;
  border: 1px solid rgba(0,0,0,0.12);
  padding: 6px 14px;
  border-radius: 999px;
  cursor: pointer;
  user-select: none;
  height: 30px;
  font-size: 12px;
  font-family: inherit;
  font-weight: 700;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  min-width: 100px;
  width: auto;
  box-sizing: border-box;
}

.custom-lang-select .lang-label {
  color: inherit;
}

.custom-lang-select .arrow {
  font-size: 0.9em;
  margin-left: 2px;
  color: inherit;
  transition: transform 0.2s;
}

.custom-lang-select .arrow.open {
  transform: rotate(180deg);
}

.custom-lang-select:hover {
  background: rgba(0,0,0,0.04);
  border-color: rgba(0,0,0,0.16);
}

/* Contenedor del selector de idioma */
.language-selector-inline {
  position: relative;
  display: inline-block;
}

/* Asegurar que el contenedor de la cuenta de usuario y el menú estén por encima */
.user-account-wrapper {
  position: relative;
  z-index: 2147483651 !important;
}

.user-menu {
  z-index: 2147483652 !important;
}

/* Estilos del dropdown de idioma */
.lang-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  color: #333;
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  min-width: 140px;
  width: 100%;
  padding: 6px 0;
  z-index: 10110;
  list-style: none;
  margin-top: 4px;
}

.lang-dropdown li {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  font-weight: 600;
  color: #333;
  background: transparent;
  transition: background 0.12s ease, color 0.12s ease;
  border-radius: 4px;
}

.lang-dropdown li:hover {
  background: #f5f5f5;
}

/* Estilos del header */
.header-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 16px; /* aumentado ~33% */
  font-weight: bold;
  color: #000;
  text-align: left;
  padding: 0;
  box-sizing: border-box;
  min-height: 40px;
  height: auto;
  flex: 0 1 auto; /* puede reducirse, pero no crecerá para empujar logo/divider */
  min-width: 100px; /* evita reducción agresiva que cause solapamiento */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.header-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0;
  background-color: #fff;
  color: #000;
  padding: 0 8px;
  position: sticky;
  top: 0;
  /* Asegurar que el header y su menú queden por encima del contenido de las vistas */
  z-index: 2147483650 !important;
  /* match working header z-index to avoid covering the manual sidebar */
  z-index: 100;
  flex-wrap: nowrap;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-bottom: 2px solid #99172A;
  overflow: visible !important;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px; /* separación reducida para compactar */
  /* ocupar aprox. 1/3 del ancho total */
  flex: 0 0 33.333%;
  max-width: 33.333%;
  box-sizing: border-box;
  padding-left: 0; /* eliminado el margen/espacio izquierdo */
}

/* Asegurar que el título no tenga margen izquierdo */
.header-title {
  margin-left: 0;
  padding-left: 0;
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  /* ocupar el resto: aprox. 2/3 */
  flex: 0 0 66.666%;
  max-width: 66.666%;
  box-sizing: border-box;
  padding-right: 8px;
  position: relative;
  z-index: 100;
  overflow: visible !important;
}

.header-logo {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex: 0 0 auto; /* tamaño basado en contenido */
  min-width: 80px;
}

.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  z-index: 2; /* por encima del fondo, pero no excesivo */
  position: relative;
  flex: 0 0 auto; /* no crecer ni encoger */
  min-width: 96px; /* aumentado ~33% */
  margin: 0;
}

.logo {
  display: block !important;
  width: auto !important;
  max-height: 48px !important; /* aumentado ~33% */
  height: auto !important;
  opacity: 1 !important;
  visibility: visible !important;
  user-select: none;
  position: relative;
  z-index: 210 !important; /* asegurar que el logo esté por encima del bloque derecho en escritorio */
  margin: 0;
  transform: none !important; /* evitar desplazamientos que provoquen solapamiento */
}

.logo-divider {
  width: 4px;
  flex: 0 0 3px; /* fijo, no se encoge ni crece */
  background-color: #99172A;
  height: calc(var(--header-height, 64px) * 0.6667);
  max-height: calc(var(--header-height, 64px) - 12px);
  align-self: center;
  margin: 0 16px; /* separación horizontal aumentada ~33% */
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(153,23,42,0.12);
  box-sizing: border-box;
  flex-shrink: 0;
  position: relative;
  z-index: 1; /* debajo del logo para evitar que el divider tape al logo */
}

@media (max-width: 600px) {
  .logo-divider { display: none; }
}

/* Iconos en fila horizontal */
.icon-row-horizontal {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px; /* reducido para compactar */
  margin-top: 0;
  position: relative;
  z-index: 100;
  overflow: visible !important;
}

/* Botones */
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 35px; /* aumentado ~33% */
  height: 35px; /* aumentado ~33% */
  border-radius: 50%;
  border: 1px solid rgba(0,0,0,0.08);
  background: transparent;
  color: #000;
  cursor: pointer;
  transition: background-color 0.12s, transform 0.08s;
  white-space: nowrap;
}

/* Ensure router-link / anchor used for admin buttons (manage-users) is not underlined */
.manage-users,
.icon-btn.manage-users,
.manage-users .btn-label,
.icon-btn.manage-users .btn-label {
  text-decoration: none !important;
}

.icon-btn.elongated {
  border-radius: 999px;
  padding: 5px 11px; /* aumentado ~33% */
  width: auto;
  height: 32px; /* aumentado ~33% */
  display: inline-flex;
  gap: 6px;
  align-items: center;
  background-color: #99172A;
  color: #fff;
}

.btn-label {
  font-size: 12px; /* aumentado ~33% */
  font-weight: 700;
  display: inline-block;
  line-height: 1;
}

.icon-btn .btn-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
  max-width: 160px;
  min-width: 0;
  flex: 0 1 auto;
  vertical-align: middle;
}

.icon-btn .material-icons,
.icon-btn img,
.icon-btn .material-icons-outlined,
.icon-btn .help-icon {
  margin-left: 6px; /* reducido */
  margin-right: 0;
  order: 2;
  color: white;
}

.icon-btn .btn-label { order: 1; }

@media (max-width: 900px) {
  .icon-btn .btn-label { max-width: 90px; }
}

.icon-btn.elongated .btn-label {
  max-width: 220px;
  min-width: 0;
  flex: 0 1 auto;
}

@media (max-width: 1200px) {
  .icon-btn.elongated .btn-label { max-width: 120px; }
}

@media (max-width: 600px) {
  /* Mostrar el texto de los botones en móvil, pero más pequeño y truncado si hace falta */
  .icon-btn .btn-label {
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    max-width: 120px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.icon-btn .material-icons { font-size: 16px; }
.icon-btn:hover { background: rgba(153,23,42,0.04); }
.icon-btn:active { transform: translateY(1px); }
.icon-btn:focus { outline: 3px solid rgba(153,23,42,0.12); outline-offset: 2px; }

/* Botón específico de cuenta */
.icon-btn.user-account {
  background: #ffffff;
  color: #000;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow: none;
}

.icon-btn.user-account .material-icons {
  color: #000 !important;
}

.icon-btn.user-account:hover {
  background: rgba(0,0,0,0.03);
}

.user-account-icon {
  width: 27px;
  height: 27px;
  display: block;
  object-fit: contain;
  filter: none;
}

/* Wrapper y menú de usuario - CORREGIDO */
.user-account-wrapper {
  position: relative;
  display: inline-block;
  z-index: 10160;
}

.user-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 12px);
  background: #ffffff;
  border: 1px solid rgba(0,0,0,0.08);
  box-shadow: 0 12px 36px rgba(0,0,0,0.18);
  border-radius: 6px;
  min-width: 213px; /* aumentado ~33% */
  z-index: 10170;
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  opacity: 1 !important;
  visibility: visible !important;
  transform: translateY(0) !important;
}

.user-menu-item {
  background: transparent;
  border: none;
  text-align: left;
  padding: 8px 10px;
  font-size: 14px;
  color: #000;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user-menu-item:hover { 
  background: rgba(0,0,0,0.04); 
}

.user-menu-item.logout {
  color: #99172A;
}

  .user-menu-item.logout img {
  width: 24px;
  height: 24px;
  margin-left: 10px;
  filter: none;
  color: inherit;
}

.user-menu-item.logout .logout-icon {
  width: 24px;
  height: 24px;
  margin-left: 10px;
  display: block;
}

/* Primary action buttons */
.header-container .icon-btn.primary-action {
  background-color: #99172A !important;
  color: #ffffff !important;
  border-color: transparent !important;
}

.header-container .icon-btn.primary-action .btn-label {
  color: #ffffff !important;
}

.header-container .icon-btn.primary-action .material-icons {
  color: #ffffff !important;
}

.help-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  margin-left: 8px;
  display: block;
}

.header-container .icon-btn.primary-action:hover {
  background-color: #7a111f !important;
}

/* Logo y título */
.logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-sizing: border-box;
  z-index: 2; /* por encima del fondo, pero no excesivo */
  position: relative;
  flex: 0 0 auto; /* no crecer ni encoger */
  margin: 0;
}

.title-inline {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

/* Separador vertical dinámico y título en mayúsculas.
   El divider usa height en em para escalar con font-size y las media queries ya definidas. */
.brand-title {
  color: #99172A;
  font-size: 16px; /* aumentado ~33% */
  margin: 0;
  line-height: 1;
  white-space: nowrap;
  text-transform: none; /* no forzar mayúsculas */
  position: relative;
  display: inline-block;
}

/* pseudo-elemento para la línea vertical a la izquierda del título */
.brand-title::before {
  content: "";
  display: inline-block;
  width: 3px;
  height: 1em; /* igual a la altura de la fuente (escala con font-size) */
  background: #99172A;
  border-radius: 2px;
  margin-right: 12px;
  vertical-align: middle;
  transform: translateY(0.05em); /* ajuste fino de alineación con texto en mayúsculas */
}

/* En pantallas pequeñas la regla seguirá reduciendo font-size vía media queries,
   con lo que la línea vertical se reducirá automáticamente. */

  .logo {
  display: block !important;
  width: auto !important;
  max-height: calc(var(--header-height, 64px) * 0.6667) !important;
  height: auto !important;
  user-select: none;
  position: relative;
  z-index: 3; /* ligeramente por encima de la divider si hay cruce involuntario */
  margin: 0;
  transform: none !important; /* evitar desplazamientos que provoquen solapamiento */
}

@media (max-width: 600px) {
  .logo-container { width: 120px; min-width: 74px; }
  .logo { width: 120px; max-height: 64px; }
  /* Mostrar el título también en móvil (pequeño) */
  .brand-title { display: inline-block; font-size: 16px; margin-left: 10px; }
}

@media (min-width: 601px) {
  /* eliminar cualquier transformación que empuje el logo sobre la divider */
  .logo { transform: none; }
  .logo { max-height: 106px !important; }
  .logo-container { min-width: 160px; }
}

/* Estilos del modal de cambio de contraseña */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 11000; /* elevado por encima de los thead de WelcomeView (10002/10003) */
  pointer-events: auto;
}

.chgpass-modal.modal-content {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 11010 !important; /* modal content debe quedar por encima de los encabezados */
  margin: 0 !important;
  width: 100%;
  max-width: 500px;
  background: #ffffff !important;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(153,19,19,0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-content {
  width: 100%;
  background: #ffffff !important;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(153,19,19,0.25);
  overflow: hidden;
  box-sizing: border-box;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  flex: 0 0 auto;
  top: 0;
  left: 0;
  border-radius: 8px;
  background-color: #99172A;
  color: white;
}

.modal-body {
  padding: 12px 16px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 120px;
}

.modal-footer {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* Estilos para inputs de contraseña */
.password-container {
  position: relative;
  display: flex;
  align-items: center;
}

.password-container .form-input {
  flex: 1;
  padding-right: 40px;
}

.password-container .password-toggle {
  position: absolute;
  right: 10px;
  cursor: pointer;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #99172A;
  box-shadow: 0 0 0 3px rgba(153,23,42,0.1);
}

.btn-primary {
  background-color: #99172A;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background-color: #7a111f;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

/* Responsive */
@media (max-width: 600px) {
  /* Móvil: apilar en filas (primera: logo/divider/título, segunda: ayuda+idioma+cuenta, tercera: acciones administrativas si están)
     Centrar todo el contenido horizontalmente */
  .header-container {
    flex-direction: column;
    align-items: center; /* centrar horizontalmente */
    justify-content: center;
    gap: 6px; /* separación entre filas */
    padding: 6px 8px;
  }

  /* En móvil, columnas ocupan 100% y se reordenan si es necesario */
  .header-left, .header-right {
    flex: 0 0 auto !important;
    max-width: 100% !important;
    width: 100% !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important; /* por defecto centrar */
  }

  .header-user-actions {
    gap: 6px;
    padding-right: 8px;
  }

  .language-selector-inline {
    order: 0;
    display: inline-flex !important;
    justify-content: center !important;
    align-items: center !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .icon-btn.elongated {
    padding: 4px 6px;
  }

  .logo { width: 90px; }
  
  .modal-content {
    max-width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 4px;
  }
  
  .chgpass-modal.modal-content {
    max-width: 95%;
  }

  /* Ajustes específicos para ordenar botones en filas: 
     - fila 2: ayuda (manual-icon), selector de idioma, cuenta de usuario
     - fila 3 (solo si existen): gestion usuarios y mantenimiento tablas */
  .icon-row-horizontal {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end; /* alinear los iconos a la derecha en móvil */
    gap: 8px;
    width: 100%;
    padding-right: 8px;
    box-sizing: border-box;
  }

  /* admin-row: tercera fila que contiene los dos botones administrativos */
  .admin-row {
    order: 3;
    display: flex; /* visible en móvil */
    width: 100%;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 6px;
    box-sizing: border-box;
    padding-right: 8px;
  }

  /* Ocultar versión de escritorio dentro de la fila en móvil */
  .desktop-admin { display: none !important; }

  .admin-row .icon-btn.manage-users,
  .admin-row .icon-btn.table-maintenance {
    flex: 0 0 40%;
    max-width: 40%;
    padding: 6px 8px;
    height: 34px;
    box-sizing: border-box;
  }

  .icon-row-horizontal .manage-users,
  .icon-row-horizontal .table-maintenance {
    /* Poner ambos botones en la misma fila y darles 40% de ancho cada uno en móvil */
    order: 3;
    flex: 0 0 40%;
    max-width: 40%;
    display: flex;
    justify-content: flex-end; /* contenido (el botón) alineado a la derecha dentro de su 40% */
    margin-top: 6px;
    padding-right: 8px;
    box-sizing: border-box;
  }

  /* Tamaño compacto para los botones administrativos (alineados a la derecha en su fila) */
  .icon-row-horizontal > .manage-users,
  .icon-row-horizontal > .table-maintenance {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
  }

  .icon-row-horizontal > .manage-users .icon-btn,
  .icon-row-horizontal > .table-maintenance .icon-btn,
  .icon-btn.manage-users,
  .icon-btn.table-maintenance {
    padding: 6px 11px;
    height: 40px;
    min-width: 106px; /* aumentado ~33% */
    margin-left: 10px;
    box-sizing: border-box;
  }

  .icon-row-horizontal > .manage-users .btn-label,
  .icon-row-horizontal > .table-maintenance .btn-label,
  .icon-btn.manage-users .btn-label,
  .icon-btn.table-maintenance .btn-label {
  font-size: 13px;
    max-width: 120px;
  }

  /* asegurar que ayuda, idioma y cuenta vayan en la segunda fila */
  .icon-row-horizontal .manual-icon,
  .language-selector-inline,
  .user-account-wrapper {
    order: 2;
  }
}

@media (min-width: 601px) {
  /* Mostrar desktop-admin en pantallas anchas */
  .desktop-admin { display: flex !important; gap: 8px; align-items: center; }

  /* Asegurar que admin-row esté oculto en escritorio */
  .admin-row { display: none !important; }
  .header-user-actions {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-end !important;
    gap: 12px;
    padding-right: 12px;
  }

  .language-selector-inline {
    display: flex !important;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding-right: 0;
  }

  .user-account-wrapper {
    margin-left: 8px;
    display: inline-flex;
    align-items: center;
  }
}
</style>

<!-- Estilos GLOBALES para modal/overlay -->
<style>
.chgpass-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
}

/* Styles for teleported language dropdown appended to body (must not be scoped) */
.lang-dropdown-teleported {
  position: absolute !important;
  background: white;
  color: #333;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 6px;
  box-shadow: 0 12px 36px rgba(0,0,0,0.18);
  padding: 6px 0;
  list-style: none;
  margin: 0;
}
.lang-dropdown-teleported li {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}
.lang-dropdown-teleported li:hover { background: #f5f5f5; }


.chgpass-modal.modal-content {
  position: fixed !important;
  top: 50% !important;
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  z-index: 20001 !important;
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 40px);
  background: #ffffff !important;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
}

.modal-body {
  overflow-y: auto !important;
  flex: 1 1 auto !important;
}

@media (max-width: 600px) {
  .chgpass-modal.modal-content { 
    max-width: 95%; 
    max-height: calc(100vh - 24px); 
    border-radius: 6px; 
  }
}

.header-container .header-title,
.header-container .header-title h1 {
  background-color: #fff !important;
}
</style>